/**
 * Page lockout while a tour runs (plan §6.4 item 1, F17).
 *
 * A pointer shield alone is not enough: a field or button that had focus
 * before the tour still takes Enter / Space / typing, and Tab walks focus into
 * whatever is under the shield. On a form that keystroke is an auto-save.
 *
 * lockPage: every child of <body> except the tour layer gets `inert` — no
 * pointer, no focus, no keyboard, out of the accessibility tree. Modals and
 * menus portal to <body>, so a MutationObserver inerts anything a tour action
 * opens while the tour is running (and takes focus back out of it). Only the
 * attributes WE added are removed afterwards, and focus goes back where it was.
 *
 * sealLayer: the opposite direction. Events that START inside the layer (Enter
 * on Next, a mousedown on the shield) would otherwise bubble on to document /
 * window listeners — page shortcuts, a modal's Tab trap, click-outside
 * handlers — and act on the page underneath. React handles them on the layer
 * itself first (portal containers get React's own listeners), so stopping
 * them at the layer in the bubble phase keeps the tour's buttons working and
 * the page deaf.
 */
export function lockPage(layer) {
  const touched = new Set();
  const previous = document.activeElement;

  const blurIfInside = (el) => {
    const a = document.activeElement;
    if (a && a !== document.body && el.contains(a)) {
      try {
        a.blur();
      } catch (_) {
        /* ignore */
      }
    }
  };

  const inertIt = (el) => {
    if (!el || el.nodeType !== 1 || el === layer) return;
    const tag = el.tagName;
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "LINK") return;
    if (el.hasAttribute("inert")) return;
    el.setAttribute("inert", "");
    touched.add(el);
    // Not every browser drops focus from an element that becomes inert.
    blurIfInside(el);
  };

  Array.from(document.body.children).forEach(inertIt);

  let observer = null;
  if (typeof MutationObserver !== "undefined") {
    observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((n) => n.parentNode === document.body && inertIt(n)));
    });
    observer.observe(document.body, { childList: true });
  }

  return function unlock() {
    if (observer) observer.disconnect();
    touched.forEach((el) => el.removeAttribute("inert"));
    touched.clear();
    // Put focus back where the user left it, if that element is still there
    // and usable. preventScroll: the tour may have scrolled elsewhere.
    if (
      previous &&
      previous !== document.body &&
      previous.isConnected &&
      !previous.closest?.("[inert]") &&
      typeof previous.focus === "function"
    ) {
      try {
        previous.focus({ preventScroll: true });
      } catch (_) {
        /* ignore */
      }
    }
  };
}

const SEALED_EVENTS = [
  "keydown",
  "keyup",
  "keypress",
  "mousedown",
  "mouseup",
  "pointerdown",
  "pointerup",
  "touchstart",
  "touchend",
  "click",
  "dblclick",
  "contextmenu",
  "paste",
  "copy",
  "cut",
  "wheel",
];

// The handler never calls preventDefault, so it can be passive — a
// non-passive wheel / touch listener on a full-screen element makes the
// browser wait on it before scrolling.
const PASSIVE = new Set(["wheel", "touchstart", "touchend"]);

export function sealLayer(layer) {
  const stop = (event) => event.stopPropagation();
  SEALED_EVENTS.forEach((type) =>
    layer.addEventListener(type, stop, PASSIVE.has(type) ? { passive: true } : undefined)
  );
  return function unseal() {
    SEALED_EVENTS.forEach((type) => layer.removeEventListener(type, stop));
  };
}
